import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableTopPanel } from 'components/TableTopPanel';
import { ProjectStatusEnum } from 'gql/graphql';
import type {
    BimContractResultsViewData,
    BimNormalContractResultData,
    BimContractConstructionTypeData,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { Loader } from 'uikit';

import type { RowClassParams, GetRowIdParams, ColDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';

import type { IBimContractResultPlanDataItem } from '../../WorkOrder/types';

import { useAccumulationSheetColumns } from './hooks/useAccumulationSheetColumns';
import { useAccumulationSheetData } from './hooks/useAccumulationSheetData';

import Style from './BimContractAccumulationSheet.module.css';

interface IBimContractAccumulationSheet {
    resultPlan?: (IBimContractResultPlanDataItem | null)[] | null;
    frameContractResults?: BimContractResultsViewData | null;
    normalContractResults?: (BimNormalContractResultData | null)[] | undefined | null;
    contractTitle: string;
    setValue?: UseFormSetValue<any>;
    gridApiRef?: React.MutableRefObject<AgGridReact<any> | null>;
    isReadOnly?: boolean;
    isLoading?: boolean;
    statusProject?: ProjectStatusEnum;
    constructionTypes?: BimContractConstructionTypeData[];
    hasNoPlanByConstructionType?: boolean | null;
    contractCalculationStatus?: WorkOrderStatusEnum;
}

const getRowClass = (params: RowClassParams<IBimContractResultPlanDataItem>) => [
    ...(params.data?.totalType?.endsWith('GROUP') ? ['is-group-line'] : []),
    ...(params.data?.isHeadLine ? ['is-head-line'] : ['is-ordinary-line']),
    ...(params.data?.isEditable ? ['is-editable'] : []),
];

const getRowId = (params: GetRowIdParams): string => {
    const { id } = params.data;

    return id;
};

export const BimContractAccumulationSheet: React.FC<IBimContractAccumulationSheet> = (props) => {
    const {
        resultPlan,
        contractTitle,
        setValue,
        gridApiRef,
        isReadOnly,
        isLoading,
        frameContractResults,
        normalContractResults,
        statusProject,
        constructionTypes,
        hasNoPlanByConstructionType,
        contractCalculationStatus,
    } = props;

    const isContractResults = useMemo(
        () => !!frameContractResults || !!normalContractResults,
        [frameContractResults, normalContractResults],
    );

    const onChange = useCallback(
        (id: string, value: number | null) => {
            setValue?.(id, value);
        },
        [setValue],
    );

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const rowData = useAccumulationSheetData({ resultPlan, frameContractResults, normalContractResults });

    const tableApi = useMemo(
        () => (gridApiRef && 'current' in gridApiRef ? gridApiRef.current : undefined),
        [gridApiRef],
    );

    const { columns, isColumnVisible, handleTableSettingsConfirm, optionalColumns } = useAccumulationSheetColumns({
        contractTitle,
        onChange,
        isReadOnly: isReadOnly || isContractResults,
        isContractResults,
        frameContractResults,
        normalContractResults,
        isProjectStatusDelayed,
        constructionTypes,
        hasNoPlanByConstructionType,
        contractCalculationStatus,
    });

    return (
        <div className={`ag-theme-alpine ${Style.bimContractAccumulationSheet}`}>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <TableTopPanel
                        optionalColumns={optionalColumns}
                        handleTableSettingsConfirm={handleTableSettingsConfirm}
                        isColumnVisible={isColumnVisible}
                        showPinColumn
                        showCheckedOnly
                        tableApi={tableApi}
                    />
                    <AgGridTable
                        rowData={rowData}
                        columnDefs={columns as ColDef[]}
                        getRowClass={getRowClass}
                        getRowId={getRowId}
                        ref={gridApiRef}
                    />
                </>
            )}
        </div>
    );
};

BimContractAccumulationSheet.displayName = 'BimContractAccumulationSheet';
